import {useMutation, useQuery} from '@apollo/client';
import {DatePicker, Form, InputNumber, Select, Spin, Switch} from 'antd';
import {GET_COMPANY_DIVIDENDS_INFO} from 'graphql/queries/startupCompanies';
import moment from 'moment';
import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import styles from './companyDividendsInfo.module.scss';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {UPDATE_COMPANY} from 'graphql/mutations/companyStartups';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';

export const CompanyDividendsInfo = ({id}) => {
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();
  const {data, loading} = useQuery(GET_COMPANY_DIVIDENDS_INFO, {
    variables: {
      companyId: id,
    },
    errorPolicy: 'all',
    onError: error => toast.error(error),
  });

  const [updateCompany, {loading: loadingUpdate}] = useMutation(
    UPDATE_COMPANY,
    {
      onError: error => toast.error(error),
      errorPolicy: 'all',
    },
  );

  useEffect(() => {
    if (data) {
      const {nextDividendPaymentDate, ...restValues} = data?.getCompany;
      form.setFieldsValue({
        ...restValues,
        nextDividendPaymentDate: nextDividendPaymentDate
          ? moment(nextDividendPaymentDate)
          : null,
      });
    }
  }, [data]);

  if (loading) {
    return (
      <div className="query-loading">
        <Spin />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <h1>Company Dividends Information</h1>
      <Form onFinish={() => setOpenModal(true)} form={form} layout="vertical">
        <Form.Item
          name="willPayDividendInCurrentYear"
          label={`Will Company Pay Dividend This Current Year(${new Date().getFullYear()})`}>
          <Switch
            defaultChecked={data?.getCompany?.willPayDividendInCurrentYear}
          />
        </Form.Item>
        <Form.Item
          className={styles.field}
          name="dividendYield"
          label="Dividend Yield (%)">
          <InputNumber
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            min={0}
            className="input-number"
          />
        </Form.Item>
        <Form.Item
          label="Dividend Payment Frequency"
          className={styles.field}
          name="dividendPaymentFrequency">
          <Select allowClear>
            <Select.Option value="ANNUAL">Annual</Select.Option>
            <Select.Option value="SEMI_ANNUAL">Semi Annual</Select.Option>
            <Select.Option value="QUARTERLY">Quarterly</Select.Option>
            <Select.Option value="MONTHLY">Monthly</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="nextDividendPaymentDate"
          label="Next Dividend Payment Date">
          <DatePicker className={styles.field} />
        </Form.Item>
        <Form.Item name="lastDividendPerShare" label="Last Dividend Per Share">
          <InputNumber
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            min={0}
            className="input-number"
          />
        </Form.Item>
        <SubmitButton label="Proceed to Save Changes" />
        <ConfirmModal
          openModal={openModal}
          setOpenModal={() => setOpenModal(false)}
          title="Save Changes"
          loading={loadingUpdate}
          confirmText="Are you sure you want to update this Company Dividends Information?"
          handleOperation={() => {
            updateCompany({
              variables: {
                input: {
                  companyId: id,
                  willPayDividendInCurrentYear:
                    form.getFieldValue('willPayDividendInCurrentYear') || false,
                  dividendYield: form.getFieldValue('dividendYield'),
                  dividendPaymentFrequency:
                    form.getFieldValue('dividendPaymentFrequency') || null,
                  nextDividendPaymentDate: form.getFieldValue(
                    'nextDividendPaymentDate',
                  )
                    ? moment(
                        form.getFieldValue('nextDividendPaymentDate'),
                      ).format()
                    : null,
                  lastDividendPerShare:
                    form.getFieldValue('lastDividendPerShare') || null,
                },
              },
            })
              .then(({data: {updateCompany}}) => {
                if (
                  updateCompany.__typename === 'Error' ||
                  updateCompany.statusCode === 400
                ) {
                  toast.error(updateCompany.message);
                } else {
                  toast.success('Updated Successfully');
                  setOpenModal(false);
                }
              })
              .catch(error => {
                toast.error(error.message);
              });
          }}
        />
      </Form>
    </div>
  );
};
